<template>
  <teleport
    v-if="rendered"
    to="#overlay"
  >
    <transition name="slide">
      <div
        v-if="showContent"
        class="ai-chat-panel"
        :style="panelStyle"
      >
        <ai-chat-surface
          :chat-id="activeChatId"
          @new-chat="newChat"
          @select-chat="openChat"
          @chat-created="showChat"
          @chat-deleted="showChat(null)"
        >
          <template #header-actions>
            <m-tooltip placement="bottom">
              <m-btn
                class="_expand"
                icon="open-as-page"
                light
                hide-border
                fab
                small
                @click="expand"
              />
              <template #title>
                {{ $t('aiChat.openFullScreen') }}
              </template>
            </m-tooltip>
            <m-tooltip placement="bottom">
              <m-btn
                class="_close"
                icon="double-right"
                light
                hide-border
                fab
                small
                @click="close"
              />
              <template #title>
                {{ $t('general.close') }}
                <br><span class="_shortcut">Escape</span>
              </template>
            </m-tooltip>
          </template>
        </ai-chat-surface>
        <div
          class="_drag-handle"
          @mousedown="handleDragStartX"
        />
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import AiChatSurface from '@/components/ai-chat/AiChatSurface.vue';
import useAiChatPanel from '@/composables/ai-chat/ai-chat-panel';
import useDragResize from 'shared/composables/drag-resize';
import useSidePanels from '@/composables/layout/side-panels';
import { MIN_WIDTH_AI_CHAT_PANEL } from '@/lib/constants';
import { aiChatRoute } from '@/lib/ai-chat-route';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { constrainWidth, maxWidth } from '@/lib/ai-chat-panel';
import { panel } from '@/lib/side-panel-layout';
import { useRouter } from 'vue-router';

const router = useRouter();
const { open, activeChatId, width, close, setWidth, newChat, openChat, showChat } = useAiChatPanel();
const { windowWidth, fitWidths, isOnTop, panelToClose } = useSidePanels();

const expand = () => router.push(aiChatRoute(activeChatId.value));

const rendered = ref(false);
const showContent = ref(false);
let unmountTimeout = null;

const { value: panelWidth, dragging, handleDragStartX } = useDragResize({
  min: MIN_WIDTH_AI_CHAT_PANEL,
  max: () => maxWidth(windowWidth.value),
  reversed: true,
  defaultValue: constrainWidth(width.value, window.innerWidth),
  onEnd: setWidth,
});

const fittedWidth = computed(() => fitWidths({
  aiChat: panelWidth.value,
  dragged: dragging.value ? panel.aiChat : null,
}).aiChat);

const panelStyle = computed(() => ({ width: `${fittedWidth.value}px` }));

watch([width, windowWidth], ([stored, available]) => {
  panelWidth.value = constrainWidth(stored, available);
});

watch(panelToClose, (toClose) => {
  if (toClose === panel.aiChat) {
    close();
  }
});

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && isOnTop(panel.aiChat)) {
    close();
  }
};

watch(open, (shown) => {
  clearTimeout(unmountTimeout);
  if (shown) {
    rendered.value = true;
    nextTick(() => { showContent.value = true; });
    return;
  }
  showContent.value = false;
  // the portal content may only be removed once the slide-out transition has ended
  unmountTimeout = setTimeout(() => { rendered.value = false; }, 250);
}, { immediate: true });

onMounted(() => { window.addEventListener('keydown', handleEscapeKey); });

onBeforeUnmount(() => {
  clearTimeout(unmountTimeout);
  window.removeEventListener('keydown', handleEscapeKey);
});
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._shortcut {
    font-size: $font-size-2;
    color: $grey-absolute-base;
  }

  .ai-chat-panel {
    display: flex;
    position: fixed;
    z-index: 1050;
    top: 0;
    right: 0;
    bottom: 0;
    background: white;
    border-left: 1px solid $border-color;
    box-shadow: -3px 0 15px $hover-color;

    ._drag-handle {
      position: absolute;
      top: 0;
      left: 0;
      width: .4rem;
      height: 100%;
      cursor: col-resize;
      background-color: transparent;
      flex-shrink: 0;
    }
  }
</style>
